// Small data-loading hook shared by the management pages: run an async loader,
// track loading/error/data, and expose reload(). Errors are decoded to a
// friendly string via errorText.

import { useCallback, useEffect, useState, type DependencyList } from "react";
import { errorText } from "../api";

export interface AsyncData<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useAsyncData<T>(load: () => Promise<T>, deps: DependencyList = []): AsyncData<T> {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    load()
      .then((d) => {
        if (!cancelled) setState({ data: d, error: null, loading: false });
      })
      .catch((e: unknown) => {
        if (!cancelled) setState({ data: null, error: errorText(e), loading: false });
      });
    return () => {
      cancelled = true;
    };
    // load is recreated each render; re-run on explicit reload (tick) + caller deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}
